import React from 'react';
import { useRouter } from 'next/router';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { useDeals } from '~/hooks/useDeals';
import { BreadcrumbItem } from '~/components/common/parts/breadcrumb/Breadcrumbs';
import { PATH as DEALS_PATH } from '~/pages/deals';
import Loading from '~/components/common/parts/loading/Loading';
import { PATH } from '~/pages/deals/[id]';
import { PATH as ESTIMATIONS_EDIT_PATH } from '~/pages/deals/[id]/estimations/edit';
import Styles from './Page.module.scss';
import { useDealEstimations } from '~/hooks/deals/useDealEstimations';
import { getDownloadPath } from '~/utils/api/deals/DealEstimationFile';
import { getDownloadPath as getHistoryDownloadPath } from '~/utils/api/deals/DealEstimationHistoryFile';
import { ConstructionTop } from '~/components/common/constructions/ConstructionTop';
import { useConstructionItems } from '~/hooks/useConstructionItems';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import { update as updateEnabled } from '~/utils/api/deals/DealEstimation';
import {
  DealConstruction,
  DealConstructionContext
} from '~/models/DealConstruction';
import {
  IN_CONSTRUCTION,
  isOwnStatus,
  OWN_SEND_ESTIMATE,
  SEND_ESTIMATE
} from '~/models/Status';
import { isItandiVendor } from '~/models/VendorCompany';
import { getOriginVendor } from '~/models/DealVendorCompany';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ConstructionConfirmedFooter } from '~/components/common/constructions/ConstructionConfirmedFooter';
import useItandiAccount from '~/hooks/useItandiAccount';
import { update, updateAccept } from '~/utils/api/deals/DealEstimationHeader';
import { NskResponse } from '~/models/Api';
import { update as updateStatus } from '~/utils/api/DealStatus';
import { useDealEstimationHeaders } from '~/hooks/deals/useDealEstimationHeaders';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { estimationRequestSide } from '~/models/DealEstimationHeader';

// 下記ステータスの場合のみ施工対象を変更できる
const allowedStatuses: number[] = [SEND_ESTIMATE, OWN_SEND_ESTIMATE];

export const Page: React.FC = () => {
  const id = useIdQueryHandler();
  const router = useRouter();
  const { data: currentAccount } = useItandiAccount().currentAccount();
  const { data: estimationContext, mutate: mutateConstructions } =
    useDealEstimations().index(id);
  const { data: deal } = useDeals().show(id);
  const { data: constructionItems } = useConstructionItems().index();
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(id);
  const { data: dealEstimationHeaders, mutate: mutateHeaders } =
    useDealEstimationHeaders().index(id);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const { data: dealEstimationRequestHistoryContext, isLoading } =
    useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
  const isReadonly = React.useMemo(() => {
    if (deal === null) {
      return true;
    }
    if (requestHeader !== null) return true;
    return !allowedStatuses.some((s) => s === deal.status);
  }, [deal, requestHeader]);
  React.useEffect(() => {
    if (deal === null) return;
    if (!isOwnStatus(deal.status) && estimationRequestSide(deal, 'client')) {
      router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
    }
  }, [deal, router]);
  if (
    id === null ||
    deal === null ||
    isLoading ||
    estimationContext === null ||
    dealEstimationHistoryContext === null ||
    constructionItems === null ||
    dealEstimationHeaders === null ||
    currentAccount === null
  ) {
    return <Loading isLoading />;
  }
  const getConstructionContextList = (): DealConstructionContext => {
    if (isOwnStatus(deal.status)) {
      if (
        dealEstimationHistoryContext &&
        dealEstimationHistoryContext?.dealConstructions.length !== 0
      ) {
        return dealEstimationHistoryContext;
      }
      return estimationContext;
    }
    return dealEstimationRequestHistoryContext ?? estimationContext;
  };
  const handleMutateConstructions = async (): Promise<void> => {
    mutateConstructions && (await mutateConstructions());
    mutateHeaders && (await mutateHeaders());
  };
  const handleClickPreview = (): void => {
    const path = getDownloadPath(id);
    window.open(path);
  };
  const handleClickHistoryPreview = (): void => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    const path = getHistoryDownloadPath(deal.id, requestHeader.id);
    window.open(path);
  };
  const breadcrumbItems: BreadcrumbItem[] = [
    { path: DEALS_PATH, caption: '案件一覧', active: false },
    { path: PATH(deal.id), caption: '案件詳細', active: false },
    { path: '', caption: '見積り確認', active: true }
  ];
  const handleUpdateEnabled = async (
    newConstruction: DealConstruction
  ): Promise<void> => {
    if (newConstruction.type !== 'DealEstimation') {
      return;
    }
    await updateEnabled(newConstruction, newConstruction.id, deal.id);
  };
  const handleAcceptUpdateStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateAccept(deal.id, requestHeader.id);
  };
  const handleUpdateRejectStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return update(deal.id, requestHeader.id, 'reject');
  };
  const handleAcceptForward = async (): Promise<NskResponse<void>> =>
    updateStatus(deal.id, IN_CONSTRUCTION, null, null);
  return (
    <DefaultV2>
      <DealLayout
        breadcrumbItems={breadcrumbItems}
        deal={deal}
        isVendor={false}
      >
        <h2 className={Styles.EstimateTitle}>見積り確認</h2>
        <ConstructionTop
          constructionContext={estimationContext}
          dealEstimationHistoryContext={dealEstimationRequestHistoryContext}
          handleClickEstimationHistoryPreview={
            requestHeader ? handleClickHistoryPreview : null
          }
          handleClickEstimationPreview={handleClickPreview}
          ownManagement={isOwnStatus(deal.status)}
          readonly={isReadonly}
        />
        <ConstructionList
          companyType="client"
          constructionContext={getConstructionContextList()}
          constructionItems={constructionItems}
          deal={deal}
          docType="estimation"
          handleMutateConstructions={handleMutateConstructions}
          handleUpdateEnabled={
            isItandiVendor(deal) || getOriginVendor(deal) === null
              ? handleUpdateEnabled
              : null
          }
          readonly={isReadonly}
          showEstimationConfirmedButton={
            (requestHeader && !isOwnStatus(deal.status)) ?? false
          }
          showHighlight={!isOwnStatus(deal.status)}
        />
        <ConstructionConfirmedFooter
          account={currentAccount}
          companyType="client"
          deal={deal}
          dealEstimationContext={estimationContext}
          dealEstimationHeader={requestHeader}
          handleAcceptForward={handleAcceptForward}
          handleMutate={handleMutateConstructions}
          handleUpdateAcceptStatus={handleAcceptUpdateStatus}
          handleUpdateRejectStatus={handleUpdateRejectStatus}
          isAdmin={currentAccount.isAdmin}
        />
      </DealLayout>
    </DefaultV2>
  );
};
